import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Check, Minus, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { ProofBar } from '../components/ProofBar';
import { MentorStrip } from '../components/MentorStrip';
import { analytics } from '../hooks/useAnalytics';

const TIERS = [
  {
    id: 'platform',
    tier: 'Start',
    name: 'The Platform',
    price: '$99/yr',
    bestFor: 'Self-directed families who need structure, not hand-holding.',
    outcome: 'Outcome: a fully organized, deadline-proof application process.',
    features: [
      'Full Ivy League Admissions Portal access',
      'Essay Studio with AI feedback',
      'School list, deadlines, and recommender tracking',
      'Document vault and resume builder',
      '45-minute strategy session included',
    ],
    cta: 'Start the Platform',
    to: '/sign-up',
    event: 'platform',
  },
  {
    id: 'kickstart',
    tier: 'Tier 1',
    name: 'Strategic Kickstart',
    price: 'From $500',
    bestFor: 'Families who need clarity and a concrete game plan.',
    outcome: 'Outcome: a written Admissions Game Plan you can execute.',
    features: [
      '90-minute strategy deep-dive',
      'Academic + extracurricular audit',
      'Ivy benchmark comparison',
      'Major positioning strategy',
      'Initial college list framework',
    ],
    cta: 'Book a Strategy Call',
    to: '/consultation',
    event: 'call',
  },
  {
    id: 'edge',
    tier: 'Tier 2',
    name: 'Competitive Edge',
    price: 'From $1,000',
    bestFor: 'Strong students who need sharper positioning.',
    outcome: 'Outcome: a differentiated profile committees remember.',
    features: [
      'Everything in Strategic Kickstart',
      'Resume and activities positioning',
      'Personal brand development',
      'Competition strategy (research, DECA, olympiads)',
      'Narrative shaping across the application',
    ],
    cta: 'Book a Strategy Call',
    to: '/consultation',
    event: 'call',
  },
  {
    id: 'elite',
    tier: 'Tier 3',
    name: 'Elite / Comprehensive',
    price: 'By application',
    priceNote: 'Engagements typically begin at $15,000',
    bestFor: 'Families who want managed execution, end to end.',
    outcome: 'Outcome: a fully managed candidacy from strategy to submission.',
    features: [
      'Everything in Competitive Edge',
      'Full essay architecture across every school',
      'Ivy student mentor + career mentor, ongoing',
      'Priority access to your lead strategist',
      'Application positioning for the full list',
    ],
    cta: 'Apply for Elite Consulting',
    to: '/consultation',
    event: 'elite',
    featured: true,
  },
];

const COMPARISON_ROWS = [
  { label: 'Ivy League Admissions Portal', values: [true, true, true, true] },
  { label: 'Strategy session', values: ['45 min', '90 min', '90 min', 'Ongoing'] },
  { label: 'Written Admissions Game Plan', values: [false, true, true, true] },
  { label: 'Narrative & activities positioning', values: [false, false, true, true] },
  { label: 'Ivy student mentor (your major)', values: [false, false, false, true] },
  { label: 'Career mentor (your target field)', values: [false, false, false, true] },
  { label: 'Full essay architecture', values: [false, false, false, true] },
  { label: 'Managed execution & accountability', values: [false, false, false, true] },
  { label: 'Direct lead-strategist access', values: [false, false, false, true] },
];

function CellValue({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-[#0F392B] mx-auto" strokeWidth={2.5} />;
  if (value === false) return <Minus className="h-4 w-4 text-[#E2E2E2] mx-auto" />;
  return <span className="text-xs font-medium text-[#0F392B]">{value}</span>;
}

export default function ConsultingPage() {
  const fireEvent = (event, location) => {
    if (event === 'platform') analytics.ctaPlatformStart(location);
    else if (event === 'elite') analytics.ctaEliteApply(location);
    else analytics.ctaStrategyCall(location);
  };

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Ivy League Admissions Consulting",
      "provider": { "@type": "EducationalOrganization", "name": "Ivy League Consulting", "url": "https://ivyleagueconsulting.org" },
      "areaServed": "Worldwide",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Admissions Consulting Engagements",
        "itemListElement": TIERS.map((t) => ({
          "@type": "Offer",
          "name": t.name,
          "description": t.bestFor,
          "price": t.id === 'platform' ? "99" : undefined,
          "priceCurrency": "USD",
          "url": `https://ivyleagueconsulting.org/consulting#${t.id}`,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "How is Ivy League Consulting different from other admissions consultants?",
          "acceptedAnswer": { "@type": "Answer", "text": "Every Elite engagement pairs the student with a current Ivy League student mentor in their major plus a career mentor in their target field, alongside a lead strategist — a level of matched mentorship most firms do not offer." } },
        { "@type": "Question", "name": "What does the $99 Platform include?",
          "acceptedAnswer": { "@type": "Answer", "text": "Full access to the Ivy League Admissions Portal: Essay Studio with AI feedback, school list and deadline tracking, resume builder, document vault, activity slots, and a 45-minute strategy session." } },
        { "@type": "Question", "name": "How selective are Elite engagements?",
          "acceptedAnswer": { "@type": "Answer", "text": "High-touch Elite engagements are capped at roughly fifty families per year to protect quality. Applications typically start at $15,000 and are by application only." } },
        { "@type": "Question", "name": "Do you guarantee admission to an Ivy League school?",
          "acceptedAnswer": { "@type": "Answer", "text": "No firm can guarantee admissions outcomes. We promise disciplined preparation, structured execution, and protection from avoidable mistakes." } },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="consulting-page">
      <SEO
        title="Ivy League Admissions Consulting — Strategy by Readiness"
        description="Ivy League admissions consulting with a matched current Ivy student mentor + career mentor + lead strategist. From $99 platform to Elite Comprehensive engagements. ~50 families per year."
        keywords="ivy league admissions consulting, college admissions consultant, ivy league mentor, college essay consultant, admissions strategy, harvard admissions help"
        schema={schema}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">Consulting</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Strategy by readiness,<br /><span className="italic text-[#D4AF37]">not package size.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Every family starts somewhere different. Choose the level of involvement your situation requires — and step up only when it earns its place.
          </p>
        </div>
      </section>

      <ProofBar location="consulting-hero" />

      {/* Staircase */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => (
            <div
              key={t.id}
              id={t.id}
              className={t.featured
                ? 'bg-[#0F392B] rounded-2xl p-6 text-white relative ring-2 ring-[#D4AF37] shadow-2xl scroll-mt-24'
                : 'bg-white rounded-2xl border border-[#E2E2E2] p-6 hover:shadow-xl transition-all duration-300 scroll-mt-24'}
              data-testid={`consulting-tier-${t.id}`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-[#0F392B] text-[10px] font-bold uppercase tracking-widest rounded-full whitespace-nowrap">By Application Only</div>
              )}
              <p className={`text-[10px] font-medium uppercase tracking-widest mb-1 text-[#D4AF37] ${t.featured ? 'mt-2' : ''}`}>{t.tier}</p>
              <h3 className="text-lg font-semibold mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</h3>
              <p className={`text-xl font-bold ${t.featured ? 'text-[#D4AF37]' : 'text-[#0F392B]'}`} style={{ fontFamily: "'Playfair Display', serif" }}>{t.price}</p>
              {t.priceNote && <p className={`text-[10px] mt-0.5 ${t.featured ? 'text-white/50' : 'text-muted-foreground'}`}>{t.priceNote}</p>}
              <p className={`text-xs italic my-4 leading-relaxed ${t.featured ? 'text-white/70' : 'text-muted-foreground'}`}>Best for: {t.bestFor}</p>
              <div className="space-y-2 mb-4">
                {t.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${t.featured ? 'text-[#D4AF37]' : 'text-[#0F392B]'}`} strokeWidth={2} />
                    <p className={`text-[11px] ${t.featured ? 'text-white/80' : 'text-muted-foreground'}`}>{f}</p>
                  </div>
                ))}
              </div>
              <p className={`text-[10px] font-medium mb-4 ${t.featured ? 'text-[#D4AF37]' : 'text-[#D4AF37]'}`}>{t.outcome}</p>
              <Link to={t.to} onClick={() => fireEvent(t.event, 'consulting-staircase')}>
                <Button className={t.featured
                  ? 'w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] h-10 text-xs font-semibold'
                  : 'w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-10 text-xs'}
                  data-testid={`consulting-cta-${t.id}`}>
                  {t.cta} <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            Prefer ongoing monthly advisory? <Link to="/premium" className="text-[#0F392B] font-medium underline underline-offset-2">Premium Advisory — $499/month</Link>. Exact engagement scope and pricing are confirmed on your strategy call.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="consulting-comparison">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">At a Glance</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              What each engagement includes
            </h2>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-[#E2E2E2] bg-white shadow-sm">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-[#E2E2E2]">
                  <th className="text-left py-4 px-5 text-xs uppercase tracking-wide text-muted-foreground font-medium">Included</th>
                  {TIERS.map(t => (
                    <th key={t.id} className="py-4 px-3 text-center">
                      <span className="text-xs font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</span>
                      <span className="block text-[10px] text-muted-foreground font-normal mt-0.5">{t.price}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? 'bg-[#F9F9F7]/60' : ''}>
                    <td className="py-3 px-5 text-xs text-foreground">{row.label}</td>
                    {row.values.map((v, j) => (
                      <td key={j} className="py-3 px-3 text-center">
                        <CellValue value={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Mentor Strip */}
      <MentorStrip
        bg="dark"
        testid="consulting-mentor-strip"
        subtext="Every Elite engagement includes a current Ivy student mentor matched to your major and target schools. Below is an anonymized snapshot of our active bench — coast to coast in the U.S. and around the world."
      />

      {/* Selectivity Block */}
      <section className="py-16 md:py-20" data-testid="consulting-selectivity">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-8 w-8 text-[#D4AF37] mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Deliberately selective. <span className="italic">By design.</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-3">
            We cap high-touch engagements at roughly fifty families per year. That cap is what makes the model work: every Elite client gets a named Ivy student mentor in their major, a career mentor in their target field, and direct access to a lead strategist.
          </p>
          <p className="text-base text-foreground font-medium leading-relaxed mb-8">
            We do not promise outcomes. We promise disciplined preparation, structured execution, and protection from avoidable mistakes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/consultation" onClick={() => analytics.ctaEliteApply('consulting-selectivity')}>
              <Button size="lg" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 font-semibold" data-testid="consulting-elite-apply-btn">
                Apply for Elite Consulting <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/diagnostic">
              <Button size="lg" variant="outline" className="h-12 px-8 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white">
                Not sure? Take the Diagnostic
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
